import React, { useState, useEffect, useCallback } from 'react';
import config from '../Config';
import Topbar from '../Components/Topbar';
import ImageSelectDialog from '../Components/ImageSelectDialog';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatEasternDate } from '../Utils/dateTime';
import { formatCentsAsCurrency } from '../Utils/money';

// Service selection component for combo creation
const ServiceSelection = React.memo(({ service, isSelected, onToggle }) => {
  const handleChange = (e) => {
    e.stopPropagation();
    onToggle(service.service_id);
  };

  return (
    <div className={`flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors ${
      isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
    }`}>
      <input
        type="checkbox"
        id={`combo-service-${service.service_id}`}
        checked={isSelected}
        onChange={handleChange}
        className="rounded text-blue-600 focus:ring-blue-500"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <label 
            htmlFor={`combo-service-${service.service_id}`}
            className="text-sm font-medium cursor-pointer text-gray-900 truncate"
          >
            {service.title}
          </label>
          <div className="text-sm font-semibold text-green-600 ml-2 flex-shrink-0">
            {formatCentsAsCurrency(service.amount, service.currency || 'CAD')}
          </div>
        </div>
      </div>
    </div>
  );
});

function Combo() {
  const [combos, setCombos] = useState([]);
  const [services, setServices] = useState([]);
  const [comboOrders, setComboOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all'); // all, selected, unselected
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image: '',
    images: [],
    videos: [],
    label: '',
    services: [],
    discount_percent: '',
    final_amount: '',
    terms_and_conditions: [''],
    active: true,
    bottom_note: '',
    pricing_text: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchCombos = useCallback(async ({ search = '', active = 'all' } = {}) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${config.apiUrl}/api/v1/admin/combo`;
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (active !== 'all') {
        if (active === 'active') params.append('active', 'true');
        if (active === 'inactive') params.append('active', 'false');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setCombos(Array.isArray(result.data) ? result.data : []);
      } else {
        throw new Error('Failed to fetch combos');
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
      setCombos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/service?disable_pagination=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to fetch services');
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    fetchCombos();
    fetchServices();
  }, [fetchCombos, fetchServices]);


  const fetchComboOrders = async (comboId) => {
    // First try to find the combo in our current data and use its orders
    const combo = combos.find(c => c.combo_id === comboId);
    if (combo && combo.orders) {
      setComboOrders(combo.orders);
      setShowOrdersModal(true);
      return;
    }

    // Fallback to API call if orders not available in current data
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/combo/orders/all?combo_id=${comboId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComboOrders(Array.isArray(data.data) ? data.data : []);
        setShowOrdersModal(true);
      } else {
        throw new Error('Failed to fetch combo orders');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      let imageUrl = formData.image;

      // Handle main image upload if imageFile exists
      if (imageFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("file", imageFile);

        const uploadResponse = await fetch(
          "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
          {
            method: "POST",
            headers: {
              "X-Client": "ADMIN",
              Authorization: `Bearer ${token}`,
            },
            body: uploadFormData,
          }
        );

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success("Main image uploaded successfully!");
        } else {
          toast.error("Failed to upload main image");
          throw new Error("Failed to upload main image");
        }
      }

      const url = isEditing 
        ? `${config.apiUrl}/api/v1/admin/combo/${editingId}`
        : `${config.apiUrl}/api/v1/admin/combo`;
        
      const method = isEditing ? 'PUT' : 'POST';

      const submitData = {
        title: formData.title,
        subtitle: formData.subtitle || null,
        description: formData.description || null,
        image: imageUrl || null,
        images: formData.images || [], // Always array, never null
        videos: formData.videos || [], // Always array, never null
        label: formData.label || null,
        services: formData.services, // Array of service IDs as strings
        discount_percent: parseFloat(formData.discount_percent) || 0,
        final_amount: parseInt(parseFloat(formData.final_amount) * 100), // Convert to cents
        terms_and_conditions: formData.terms_and_conditions.filter(term => term.trim()) || [], // Always array, never null
        active: formData.active,
        bottom_note: formData.bottom_note || null,
        pricing_text: formData.pricing_text || null
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Combo updated successfully!' : 'Combo created successfully!');
        setShowModal(false);
        resetForm();
        fetchCombos();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to save combo');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (combo) => {
    setFormData({
      title: combo.title || '',
      subtitle: combo.subtitle || '',
      description: combo.description || '',
      image: combo.image || '',
      images: Array.isArray(combo.images) ? combo.images : [], // Ensure array
      videos: Array.isArray(combo.videos) ? combo.videos : [], // Ensure array
      label: combo.label || '',
      services: combo.services || [],
      discount_percent: combo.discount_percent ? combo.discount_percent.toString() : '',
      final_amount: combo.final_amount ? (combo.final_amount / 100).toString() : '',
      terms_and_conditions: Array.isArray(combo.terms_and_conditions) && combo.terms_and_conditions.length > 0 
        ? combo.terms_and_conditions 
        : [''], // Always array with at least one empty string for editing
      active: combo.active !== undefined ? combo.active : true,
      bottom_note: combo.bottom_note || '',
      pricing_text: combo.pricing_text || ''
    });
    setIsEditing(true);
    setEditingId(combo.combo_id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this combo?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiUrl}/api/v1/admin/combo/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          toast.success('Combo deleted successfully!');
          fetchCombos();
        } else {
          toast.error('Failed to delete combo');
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      image: '',
      images: [],
      videos: [],
      label: '',
      services: [],
      discount_percent: '',
      final_amount: '',
      terms_and_conditions: [''], // Always array with at least one empty string
      active: true,
      bottom_note: '',
      pricing_text: ''
    });
    setImageFile(null);
    setImagePreview("");
    setIsEditing(false);
    setEditingId(null);
    setServiceSearchTerm('');
    setServiceFilter('all');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceToggle = useCallback((serviceId) => {
    setFormData(prev => {
      const currentServices = prev.services || [];
      const isSelected = currentServices.includes(serviceId);
      
      return {
        ...prev,
        services: isSelected 
          ? currentServices.filter(id => id !== serviceId)
          : [...currentServices, serviceId]
      };
    });
  }, []);

  // Calculate total price of selected services
  const calculateSelectedServicesTotal = () => {
    return formData.services.reduce((total, serviceId) => {
      const service = services.find(s => s.service_id === serviceId);
      return total + (service ? service.amount : 0);
    }, 0);
  };

  // Filter services based on search term and filter
  const filteredServices = services.filter(service => {
    // Search filter
    const matchesSearch = !serviceSearchTerm || 
      service.title.toLowerCase().includes(serviceSearchTerm.toLowerCase());
    
    // Selection filter
    const isSelected = formData.services.includes(service.service_id);
    let matchesFilter = true;
    
    if (serviceFilter === 'selected') {
      matchesFilter = isSelected;
    } else if (serviceFilter === 'unselected') {
      matchesFilter = !isSelected;
    }
    
    return matchesSearch && matchesFilter;
  });

  const handleTermsChange = (index, value) => {
    setFormData(prev => {
      const currentTerms = Array.isArray(prev.terms_and_conditions) ? prev.terms_and_conditions : [''];
      const newTerms = [...currentTerms];
      newTerms[index] = value;
      return { ...prev, terms_and_conditions: newTerms };
    });
  };

  const addTerm = () => {
    setFormData(prev => ({
      ...prev,
      terms_and_conditions: [...(Array.isArray(prev.terms_and_conditions) ? prev.terms_and_conditions : []), '']
    }));
  };

  const removeTerm = (index) => {
    setFormData(prev => {
      const currentTerms = Array.isArray(prev.terms_and_conditions) ? prev.terms_and_conditions : [''];
      const newTerms = currentTerms.filter((_, i) => i !== index);
      // Ensure at least one empty term for editing
      return {
        ...prev,
        terms_and_conditions: newTerms.length > 0 ? newTerms : ['']
      };
    });
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAdditionalImageUpload = async (file, index) => {
    const token = localStorage.getItem("token");
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const uploadResponse = await fetch(
        "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
        {
          method: "POST",
          headers: {
            "X-Client": "ADMIN",
            Authorization: `Bearer ${token}`,
          },
          body: uploadFormData,
        }
      );

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        const imageUrl = uploadData[0];
        
        const newImages = [...formData.images];
        newImages[index] = imageUrl;
        setFormData({ ...formData, images: newImages });
        
        toast.success("Image uploaded successfully!");
      } else {
        toast.error("Failed to upload image");
        throw new Error("Failed to upload image");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };


  const addVideo = () => {
    const videoUrl = prompt('Enter video URL:');
    if (videoUrl) {
      setFormData(prev => ({
        ...prev,
        videos: [...(Array.isArray(prev.videos) ? prev.videos : []), videoUrl]
      }));
    }
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: Array.isArray(prev.videos) ? prev.videos.filter((_, i) => i !== index) : []
    }));
  };

  const showDetails = (combo) => {
    setSelectedCombo(combo);
    setShowDetailsModal(true);
  };

  const formatCurrency = (amountCents) =>
    formatCentsAsCurrency(amountCents, 'CAD');

  const handleSearch = () => {
    fetchCombos({ search: searchTerm, active: activeFilter });
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setTimeout(() => fetchCombos({ search: searchTerm, active: filter }), 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Topbar />
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Topbar />
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="text-red-500 text-lg sm:text-xl text-center">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <div className="container mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-left">Combo Services</h1>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto shrink-0"
          >
            Add New Combo
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-gray-500 hover:bg-gray-700 text-white px-4 py-2 rounded"
            >
              Search
            </button>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded ${activeFilter === 'all' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              All
            </button>
            <button
              onClick={() => handleFilterChange('active')}
              className={`px-4 py-2 rounded ${activeFilter === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Active
            </button>
            <button
              onClick={() => handleFilterChange('inactive')}
              className={`px-4 py-2 rounded ${activeFilter === 'inactive' 
                ? 'bg-red-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Combos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {combos.map((combo) => (
            <div key={combo.combo_id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {combo.image && (
                <img 
                  src={combo.image} 
                  alt={combo.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-800 truncate">
                    {combo.title}
                  </h3>
                  <div className="flex space-x-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      combo.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {combo.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-2">{combo.subtitle}</p>
                
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(combo.final_amount)}
                    </span>
                  </div>
                  {combo.discount_percent > 0 && (
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                      {combo.discount_percent}% OFF
                    </span>
                  )}
                </div>
                
                <div className="text-sm text-gray-500 mb-3">
                  {combo.services?.length || 0} services
                  {combo.label && (
                    <span className="ml-2 text-blue-600">• {combo.label}</span>
                  )}
                </div>
                
                <div className="flex justify-between space-x-2">
                  <button
                    onClick={() => showDetails(combo)}
                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleEdit(combo)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-3 py-1 rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => fetchComboOrders(combo.combo_id)}
                    className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-3 py-1 rounded"
                  >
                    Orders
                  </button>
                  <button
                    onClick={() => handleDelete(combo.combo_id)}
                    className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {combos.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500">No combos found</div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {isEditing ? 'Edit Combo' : 'Add New Combo'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Final Amount (CAD) *</label>
                  <input
                    type="number"
                    name="final_amount"
                    value={formData.final_amount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Enter final price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount % *</label>
                  <input
                    type="number"
                    name="discount_percent"
                    value={formData.discount_percent}
                    onChange={handleInputChange}
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Enter discount percentage"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Label</label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Optional label for the combo"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Main Image</label>
                <div className="flex items-center space-x-2">
                  {imagePreview && (
                    <div className="mb-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-md"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowImageDialog(true);
                    }}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    {imagePreview ? "Edit Image" : "Upload Image"}
                  </button>
                  {formData.image && !imagePreview && (
                    <div className="flex items-center space-x-2">
                      <img src={formData.image} alt="Preview" className="w-12 h-12 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Images
                </label>
                <div className="space-y-2">
                  {formData.images.map((image, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      {image && (
                        <div className="flex-shrink-0">
                          <img
                            src={image}
                            alt={`Preview ${index + 1}`}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="text"
                        value={image}
                        onChange={(e) => {
                          const newImages = [...formData.images];
                          newImages[index] = e.target.value;
                          setFormData({ ...formData, images: newImages });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter image URL or upload image"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUploadingImageIndex(index);
                          setShowImageDialog(true);
                        }}
                        className="px-3 py-2 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50"
                      >
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = formData.images.filter((_, i) => i !== index);
                          setFormData({ ...formData, images: newImages });
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        images: [...formData.images, ""]
                      });
                    }}
                    className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                  >
                    Add Image
                  </button>
                </div>
              </div>

              {/* Videos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Videos</label>
                <button
                  type="button"
                  onClick={addVideo}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded mb-2"
                >
                  Add Video URL
                </button>
                <div className="space-y-2">
                  {(Array.isArray(formData.videos) ? formData.videos : []).map((video, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="url"
                        value={video}
                        readOnly
                        className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={() => removeVideo(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions</label>
                <div className="space-y-2">
                  {formData.terms_and_conditions.map((term, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={term}
                        onChange={(e) => handleTermsChange(index, e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-md"
                        placeholder={`Term ${index + 1}`}
                      />
                      {formData.terms_and_conditions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTerm(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTerm}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    Add Term
                  </button>
                </div>
              </div>

              {/* Settings */}
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>

              {/* Bottom Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bottom Note
                </label>
                <textarea
                  name="bottom_note"
                  value={formData.bottom_note}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter bottom note (optional)"
                />
              </div>

              {/* Pricing Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pricing Text
                </label>
                <textarea
                  name="pricing_text"
                  value={formData.pricing_text}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter pricing text (optional)"
                />
              </div>

              {/* Service Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Services for Combo *
                </label>
                
                {/* Search and Filter Controls */}
                <div className="mb-3 space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search services by name..."
                        value={serviceSearchTerm}
                        onChange={(e) => setServiceSearchTerm(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setServiceFilter('all')}
                        className={`px-3 py-2 text-xs rounded-md font-medium transition-colors ${
                          serviceFilter === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        All ({services.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setServiceFilter('selected')}
                        className={`px-3 py-2 text-xs rounded-md font-medium transition-colors ${
                          serviceFilter === 'selected'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Selected ({formData.services.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setServiceFilter('unselected')}
                        className={`px-3 py-2 text-xs rounded-md font-medium transition-colors ${
                          serviceFilter === 'unselected'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Available ({services.length - formData.services.length})
                      </button>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        services: services.map(s => s.service_id) 
                      }))}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, services: [] }))}
                      className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Clear All
                    </button>
                    {serviceSearchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          const filteredIds = filteredServices.map(s => s.service_id);
                          setFormData(prev => ({ 
                            ...prev, 
                            services: [...new Set([...prev.services, ...filteredIds])]
                          }));
                        }}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                      >
                        Select Filtered
                      </button>
                    )}
                  </div>
                </div>

                {/* Service List */}
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-2">
                  {filteredServices.length > 0 ? (
                    filteredServices.map((service) => (
                      <ServiceSelection
                        key={service.service_id}
                        service={service}
                        isSelected={formData.services.includes(service.service_id)}
                        onToggle={handleServiceToggle}
                      />
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {serviceSearchTerm ? (
                        <div>
                          <div className="text-sm">No services found matching "{serviceSearchTerm}"</div>
                          <button
                            type="button"
                            onClick={() => setServiceSearchTerm('')}
                            className="text-xs text-blue-500 hover:text-blue-700 mt-1"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : serviceFilter === 'selected' ? (
                        'No services selected yet'
                      ) : serviceFilter === 'unselected' ? (
                        'All services are already selected'
                      ) : (
                        'No services available'
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between items-center text-sm mt-2">
                  <div className="text-gray-500">
                    <span>Selected: {formData.services.length} of {services.length} services</span>
                    {serviceSearchTerm && (
                      <span className="ml-2">| Showing: {filteredServices.length} services</span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-green-600">
                      Total: {formatCentsAsCurrency(calculateSelectedServicesTotal(), 'CAD')}
                    </div>
                    <div className="text-xs text-gray-500">
                      Combined services value
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  {isEditing ? 'Update' : 'Create'} Combo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCombo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Combo Details</h2>
            
            <div className="space-y-4">
              {selectedCombo.image && (
                <div>
                  <img 
                    src={selectedCombo.image} 
                    alt={selectedCombo.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <div className="mt-1 text-lg font-semibold">{selectedCombo.title}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                  <div className="mt-1">{selectedCombo.subtitle || 'N/A'}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <div className="mt-1">{selectedCombo.description || 'No description'}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Final Amount</label>
                  <div className="mt-1 text-lg font-semibold text-green-600">
                    {formatCurrency(selectedCombo.final_amount)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount</label>
                  <div className="mt-1 text-lg text-red-600">
                    {selectedCombo.discount_percent}% OFF
                  </div>
                </div>
              </div>

              {selectedCombo.label && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Label</label>
                  <div className="mt-1">{selectedCombo.label}</div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedCombo.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedCombo.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {selectedCombo.services && selectedCombo.services.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Included Services</label>
                  <div className="mt-1 space-y-1">
                    {selectedCombo.services.map((service, index) => {
                      const serviceData = services.find(s => s.service_id === service.service_id);
                      return serviceData ? (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span>{serviceData.title}</span>
                          <span className="text-green-600 font-medium">
                            {formatCentsAsCurrency(serviceData.amount, serviceData.currency || 'CAD')}
                          </span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {Array.isArray(selectedCombo.terms_and_conditions) && selectedCombo.terms_and_conditions.filter(term => term.trim()).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    {selectedCombo.terms_and_conditions.filter(term => term.trim()).map((term, index) => (
                      <li key={index} className="text-sm">{term}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Modal */}
      {showOrdersModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Combo Orders</h2>
            
            {comboOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Order ID
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        User ID
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comboOrders.map((order) => (
                      <tr key={order.order_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm">{order.order_id}</td>
                        <td className="px-4 py-2 text-sm">{order.user_id || 'N/A'}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            order.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">{formatCurrency(order.expected_amount || 0)}</td>
                        <td className="px-4 py-2 text-sm">
                          {order.created_at ? formatEasternDate(order.created_at) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500">No orders found for this combo</div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowOrdersModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Selection Dialog */}
      <ImageSelectDialog
        open={showImageDialog}
        onClose={() => {
          setShowImageDialog(false);
          setUploadingImageIndex(null);
        }}
        onFileProcessed={(file) => {
          if (uploadingImageIndex !== null) {
            // Handle additional image upload
            handleAdditionalImageUpload(file, uploadingImageIndex);
            setUploadingImageIndex(null);
          } else {
            // Handle main image
            handleImageSelect(file);
          }
          setShowImageDialog(false);
        }}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default Combo;
